import { useEffect, useMemo, useRef } from "react";
import { configureMatomoHeatmapSessionRecordingForVirtualPageViews, trackMatomoPageView } from "../../../others/tracking.js";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
import { CheckoutMatomoUiStep, resolveReadyUiStep } from "../utils/matomoUiStep.js";
// Stable slug map for the per-step virtual page-view URL — anchors the
// funnel reports in Matomo to a known set of paths regardless of which
// language the buyer is in or how the WordPress page is named.
const CHECKOUT_STEP_SLUGS = {
    [CheckoutMatomoUiStep.PersonalInformation]: {
        en: CheckoutMatomoUiStep.PersonalInformation,
        de: "persoenliche-daten"
    },
    [CheckoutMatomoUiStep.Payment]: {
        en: CheckoutMatomoUiStep.Payment,
        de: "zahlung"
    },
    [CheckoutMatomoUiStep.Confirmation]: {
        en: CheckoutMatomoUiStep.Confirmation,
        de: "bestaetigung"
    }
};
const KNOWN_CHECKOUT_PATH_SUFFIXES = [
    ...Object.values(CHECKOUT_STEP_SLUGS).flatMap(({ en, de })=>[
            en,
            de
        ]),
    "download"
];
function normalizeCheckoutBasePath(pathname) {
    let normalized = pathname.replace(/\/+$/, "") || "/";
    // The checkout router mutates the browser path per step. Strip known step suffixes
    // so virtual Matomo URLs can always be rebuilt from the stable checkout base path.
    for (const suffix of KNOWN_CHECKOUT_PATH_SUFFIXES){
        if (normalized.endsWith(`/${suffix}`)) {
            normalized = normalized.slice(0, -(suffix.length + 1)) || "/";
            break;
        }
    }
    return normalized;
}
function resolveIsGermanCheckout(basePath) {
    return basePath === "/de/bestellen" || basePath.startsWith("/de/bestellen/");
}
function resolveStepSlug(basePath, step) {
    const isGermanCheckout = resolveIsGermanCheckout(basePath);
    return CHECKOUT_STEP_SLUGS[step][isGermanCheckout ? "de" : "en"];
}
function resolveLocalizedStepTitle(step) {
    switch(step){
        case CheckoutMatomoUiStep.PersonalInformation:
            return __("Personal information");
        case CheckoutMatomoUiStep.Payment:
            return __("Payment");
        case CheckoutMatomoUiStep.Confirmation:
            return __("Confirmation");
    }
}
/**
 * Build the Matomo `setDocumentTitle` value for one wizard step.
 *
 * Format: `"Checkout > <Step> - <Product>"` — the `>` separator between
 * anchor and step makes the funnel hierarchy obvious at a glance in
 * Matomo's Page-Title reports, while ` - ` between step and product
 * keeps the per-product segmentation suffix readable. The site brand is
 * intentionally omitted (Matomo already knows the site via `setSiteId`).
 */ function buildMatomoStepTitle(localizedStepTitle, productName) {
    return `${__("Checkout")} > ${localizedStepTitle} - ${productName}`;
}
/**
 * Tracks a virtual Matomo page view per wizard step (personal information
 * → payment → confirmation) so funnel reporting keeps step granularity.
 * The actual checkout URL stays stable across all steps; the per-step
 * `setCustomUrl` push synthesises a virtual path so Matomo distinguishes
 * the funnel stages even though the browser never navigates.
 */ function useCheckoutV2StepInvokeMatomoPageView() {
    const { checkoutV2Store } = useStores();
    const lastTrackedSignatureRef = useRef();
    const lastTrackedUrlRef = useRef();
    const checkoutBasePathRef = useRef(normalizeCheckoutBasePath(window.location.pathname));
    useEffect(()=>{
        configureMatomoHeatmapSessionRecordingForVirtualPageViews();
    }, []);
    const pageView = useMemo(()=>{
        if (!checkoutV2Store.product) {
            return undefined;
        }
        const readyStep = resolveReadyUiStep(checkoutV2Store.step, checkoutV2Store.view, checkoutV2Store.loading);
        if (!readyStep) {
            return undefined;
        }
        const checkoutBasePath = checkoutBasePathRef.current;
        const stepSlug = resolveStepSlug(checkoutBasePath, readyStep);
        const customUrl = `${window.location.origin}${checkoutBasePath}/${stepSlug}/`;
        const localizedStepTitle = resolveLocalizedStepTitle(readyStep);
        const productName = checkoutV2Store.product.data.productPlan.product.name;
        const documentTitle = buildMatomoStepTitle(localizedStepTitle, productName);
        return {
            // Include the product in the signature so a swap between products
            // (e.g. plan-change mid-flow, however unlikely) re-fires.
            signature: `${readyStep}:${productName}:${customUrl}`,
            customUrl,
            documentTitle
        };
    }, [
        checkoutV2Store.product,
        checkoutV2Store.step,
        checkoutV2Store.view,
        checkoutV2Store.loading
    ]);
    useEffect(()=>{
        if (!pageView || lastTrackedSignatureRef.current === pageView.signature) {
            return undefined;
        }
        // Wait until the current React commit has painted so Matomo captures the
        // DOM of the actual visible checkout step, not the step transition in between.
        const animationFrame = window.requestAnimationFrame(()=>{
            trackMatomoPageView({
                customUrl: pageView.customUrl,
                documentTitle: pageView.documentTitle,
                referrerUrl: lastTrackedUrlRef.current
            });
            lastTrackedSignatureRef.current = pageView.signature;
            lastTrackedUrlRef.current = pageView.customUrl;
        });
        return ()=>window.cancelAnimationFrame(animationFrame);
    }, [
        pageView
    ]);
}
export { useCheckoutV2StepInvokeMatomoPageView };
